/*
 * Copyright 1999-2024 Jagex Ltd.
 */
import axios, { AxiosError, AxiosResponse } from 'axios';
import { StatusCode } from '@common/constants';
import { API_URL } from '@common/envs';
import { ERROR_CODE_MESSAGES, ERROR_STATUS_MESSAGES } from '@common/messages';
import { HttpResponseNetworkError, NetworkError, ValidationResult, ValidationError } from '@common/types';

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

// Simplifying this check since we need only data usually.
const isAxiosResponse = (obj: any): obj is AxiosResponse => Boolean(obj.data);

const isHttpResponseNetworkError = (error?: NetworkError | any): error is HttpResponseNetworkError =>
  error?.errorType === 'httpResponse';

/**
 * Message extractor. If message exists in response (if response even exists), use it, otherwise fallback to override,
 * and otherwise use the default error message.
 * @param response
 * @param overrideMessage
 */
export const extractErrorMessage = (response?: AxiosResponse<NetworkError> | any, overrideMessage?: string): string => {
  const fallbackMessage = overrideMessage || ERROR_CODE_MESSAGES.GENERAL_ERROR;

  if (!isAxiosResponse(response)) {
    return ERROR_CODE_MESSAGES[`${fallbackMessage}`] ?? fallbackMessage;
  }

  if (isHttpResponseNetworkError(response.data)) {
    const { data } = response.data;

    return ERROR_CODE_MESSAGES[`${data.code}`] ?? fallbackMessage;
  }

  return ERROR_STATUS_MESSAGES[`${response.status}`] ?? fallbackMessage;
};

/**
 * Status extractor. If status exists in response (if response even exists), use it, otherwise InternalServerError (500)
 * @param response
 */
export const extractErrorStatus = (response?: AxiosResponse<NetworkError> | any): number => {
  if (!isAxiosResponse(response)) {
    return response.status;
  }

  if (isHttpResponseNetworkError(response.data)) {
    const { data } = response.data;

    return data.status ?? StatusCode.InternalServerError;
  }

  return response.status ?? StatusCode.InternalServerError;
};

export const extractAxiosErrorResponse = (error: AxiosError | any): AxiosResponse | any => error?.response ?? error;

export const checkErrorStatusCode = (
  expectedStatusCode: number | number[],
  response?: AxiosResponse<NetworkError> | any
): boolean => {
  const statusCode = extractErrorStatus(extractAxiosErrorResponse(response));

  if (Array.isArray(expectedStatusCode)) {
    return expectedStatusCode.includes(statusCode);
  }

  return expectedStatusCode === statusCode;
};

export const extractResponse = <T>(request: Promise<AxiosResponse<T>>): Promise<T> =>
  request.then(response => response.data);

export const isNetworkError = (error?: NetworkError | any): error is NetworkError =>
  error?.errorType === 'httpResponse' || error?.errorType === 'general';

export const extractValidationErrorResponse = (
  e: AxiosError | Error,
  enqueueNotification: (message: string) => void
): any => {
  const error = extractAxiosErrorResponse(e);

  if (
    isAxiosResponse(error) &&
    isNetworkError(error.data) &&
    (error.data as HttpResponseNetworkError).data.code === 'INVALID_PARAMETERS'
  ) {
    const { additionalFields } = (error.data as HttpResponseNetworkError).data;
    const validationErrors: ValidationResult[] = additionalFields?.errors ?? [];

    if (validationErrors.length > 0) {
      throw new ValidationError(...validationErrors);
    }
  }
  enqueueNotification(extractErrorMessage(error));

  return Promise.reject(e);
};
